import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import SignalCardBody from "@/components/salgsmuligheter/SignalCardBody";
import type { DryRunResult } from "@/hooks/use-run-signal-agents";
import { formatNOK } from "@/lib/format";
import { SIGNAL_STRENGTH_LABELS, signalSourceLabel } from "@/lib/signal-types";

interface Props {
  isPending: boolean;
  result: DryRunResult | undefined;
  customerNames: string[];
}

/** Viser kortene en prøvekjøring ville laget, uten at noe er lagret. Samme innhold som signalkortet i køen. */
export default function AgentDryRunPanel({ isPending, result, customerNames }: Props) {
  if (isPending) {
    return (
      <div className="space-y-2" aria-live="polite">
        <p className="text-sm text-muted-foreground">Prøvekjører mot {customerNames.join(", ") || "kundene"} …</p>
        <Skeleton className="h-24" />
      </div>
    );
  }
  if (!result) return null;

  const cost = result.runs.reduce((sum, r) => sum + (r.cost_nok ?? 0), 0);
  const failed = result.runs.filter((r) => r.status === "failed");

  return (
    <div className="space-y-3" aria-live="polite">
      <p className="text-sm text-muted-foreground">
        Prøvekjøring mot {customerNames.join(", ") || "kundene"}. Ingenting er lagret. Kostnad {formatNOK(cost)}.
      </p>
      {failed.length > 0 && (
        <p className="text-sm text-destructive">{failed.length} av {result.runs.length} kjøringer feilet. Kortene under kan være ufullstendige.</p>
      )}
      {result.signals.length === 0 ? (
        <p className="rounded border border-dashed border-border p-3 text-sm">
          Ingen signaler funnet. Prøv en bredere instruks, flere kilder eller andre kunder.
        </p>
      ) : (
        <ul className="space-y-3">
          {result.signals.map((s, i) => (
            <li key={`${s.customer_id}-${i}`} className="rounded border border-border p-3">
              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{s.customer_name ?? "Kunde"}</span>
                <Badge variant="outline">{signalSourceLabel(s.source)}</Badge>
                <Badge variant="secondary">{SIGNAL_STRENGTH_LABELS[s.strength]}</Badge>
              </div>
              <SignalCardBody signal={s} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
